import {
    IBase,
    IFaq,
    IFilter,
    IGlossary,
    IHyperlink,
    IReport,
    IReportTemplate,
    IResponsible,
    IUser,
    ResponsibleType
} from '@shared/interfaces';
import {
    FaqService,
    FilterService,
    GlossaryService,
    HyperlinkService,
    ReportService,
    ReportTemplateService,
    ResponsibleService,
    UserService
} from '@shared/services';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { Location } from '@angular/common';
import { BehaviorSubject, Subscription } from 'rxjs';
import { AdminFaqService } from '@app/main/admin/modules/admin-faq/services/admin-faq.service';
import { AdminReportService } from '@app/main/admin/modules/admin-report/admin-report-service';
import { millisecondsToDays } from '@shared/helpers/helpers';
import { IPaginatedResult } from '@shared/interfaces/mailing/IPaginatedResult';

@Injectable({
    providedIn: 'root'
})
export class AdminService implements OnDestroy {
    subscriptions: Subscription[] = [];

    toolbarState: ToolbarStateAdmin;
    toolbarType: ToolbarTypeAdmin;
    _data: IBase[];
    data: IBase[];
    _paginatedData: IPaginatedResult<IBase>;
    paginatedData: IPaginatedResult<IBase>;
    selectedItem: IBase;
    sortActive: boolean;
    shouldScroll: boolean;
    query: string;

    toolbarChanged: BehaviorSubject<ToolbarInfoAdmin> = new BehaviorSubject<ToolbarInfoAdmin>(null);
    dataChanged: BehaviorSubject<IBase[]> = new BehaviorSubject<IBase[]>(null);
    selectedItemChanged: BehaviorSubject<IBase> = new BehaviorSubject<IBase>(null);
    changeFilter: BehaviorSubject<IFilter | string> = new BehaviorSubject<IFilter | string>(null);
    scrollTop: number;

    private readonly templateService = inject(ReportTemplateService);

    constructor(
        private faqService: FaqService,
        private responsibleService: ResponsibleService,
        private userService: UserService,
        private hyperlinkService: HyperlinkService,
        private glossaryService: GlossaryService,
        private adminFaqService: AdminFaqService,
        private reportService: ReportService,
        private location: Location,
        private adminReportService: AdminReportService,
        private filterService: FilterService
    ) {
        this.subscriptions.push(
            this.toolbarChanged.subscribe(async e => {
                if (e) {
                    this.shouldScroll = true;
                    if (this.toolbarType !== e.type) {
                        this.data = this.paginatedData = null;
                        this.dataChanged.next(this.data);
                        this.scrollTop = 0;
                        this.shouldScroll = false;

                        this.adminReportService.reset();
                        this.filterService.reset();
                    }

                    this.toolbarState = e?.state;
                    this.toolbarType = e?.type;
                    this.selectedItem = e?.selectedItem;
                    this.sortActive = e?.sort;

                    if (!e.noReload) {
                        if (e?.state === ToolbarStateAdmin.Standard) {
                            this.loadData(e.type, e.query ?? null);
                        } else if (this.selectedItem) {
                            this.loadItem(e.type);
                        }
                    } else {
                        if (e.items) {
                            this.data = e.items;
                            this.dataChanged.next(this.data);
                        }
                        if (e.selectedItem) {
                            this.selectedItem = e.selectedItem;
                            this.selectedItemChanged.next(this.selectedItem);
                        }
                    }

                    let path = '';
                    switch (e.state) {
                        case ToolbarStateAdmin.Info:
                            path = `/admin/${e.type ?? ''}/info/${encodeURIComponent(this.selectedItem.id)}`;
                            break;
                        case ToolbarStateAdmin.Standard:
                            path = `/admin/${e.type ?? ''}`;
                            break;
                        case ToolbarStateAdmin.Create:
                            path = `/admin/${e.type ?? ''}/create`;
                            if (this.selectedItem) {
                                path += `/${encodeURIComponent(this.selectedItem.id)}`;
                            }
                            break;
                        case ToolbarStateAdmin.Edit:
                            path = `/admin/${e.type ?? ''}/edit/${encodeURIComponent(this.selectedItem.id)}`;
                            break;
                    }
                    this.location.replaceState(path);
                }
            })
        );
    }

    private async loadData(type: ToolbarTypeAdmin, query?: string) {
        this.scrollToPreviousLocation();
        try {
            let promise: Promise<IBase[] | IPaginatedResult<IBase>>;
            switch (this.toolbarType) {
                case ToolbarTypeAdmin.Users:
                    promise = this.loadUsers();
                    break;
                case ToolbarTypeAdmin.Reports:
                    promise = this.loadReports();
                    break;
                case ToolbarTypeAdmin.ReportTemplates:
                    promise = this.loadTemplates();
                    break;
                case ToolbarTypeAdmin.Hyperlinks:
                    promise = this.loadHyperlinks();
                    break;
                case ToolbarTypeAdmin.Glossary:
                    promise = this.loadGlossary();
                    break;
                case ToolbarTypeAdmin.Faq:
                    promise = this.loadFaq();
                    break;
                case ToolbarTypeAdmin.Responsibilities:
                    promise = this.loadResponsibilities(query as ResponsibleType);
                    break;
            }

            if (promise) {
                const result = await promise;
                if (result instanceof Array) {
                    this.data = this._data = this.toolbarType === type ? result : null;
                } else if (result.items) {
                    this.paginatedData = this._paginatedData = this.toolbarType === type ? result : null;
                } else {
                    this.selectedItem = (this.toolbarType === type ? result : null) as IBase;
                }
            }
        } catch (err) {
            console.error('loadData', err);
        }

        this.dataChanged.next(this.data ?? this.paginatedData?.items);
    }

    scrollToPreviousLocation() {
        if (this.shouldScroll && this.scrollTop) {
            setTimeout(() => document.getElementById('itemContainer')?.scrollTo({ top: this.scrollTop }), 50);
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(e => e.unsubscribe());
        this.toolbarChanged.next(null);
    }

    private async loadUsers(): Promise<IPaginatedResult<IUser>> {
        try {
            const items = await this.userService.getAllUsers();
            items.items = items.items.map(e => {
                const leftDays = millisecondsToDays(new Date(e.certificateExpiration).getTime() - new Date().getTime());
                e.certificateExpiring = leftDays <= 30 || leftDays <= 0 || !leftDays;
                return e;
            });

            return items;
        } catch (err) {
            console.error('loadUsers', err);
        }
    }

    private async loadFaq(): Promise<IFaq[]> {
        try {
            return this.faqService.getAllForAdmin();
        } catch (err) {
            console.error('loadFaq', err);
        }
    }

    private async loadResponsibilities(type?: ResponsibleType): Promise<IPaginatedResult<IResponsible>> {
        try {
            return this.responsibleService.getAll({
                type: type ?? ResponsibleType.all
            });
        } catch (err) {
            console.error('loadResponsibilities', err);
        }
    }

    private async loadHyperlinks(): Promise<IHyperlink[]> {
        try {
            return this.hyperlinkService.getAllItems();
        } catch (err) {
            console.error('loadHyperlinks', err);
        }
    }

    private async loadGlossary(): Promise<IGlossary[]> {
        try {
            return this.glossaryService.getAllItems();
        } catch (err) {
            console.error('loadGlossary', err);
        }
    }

    private async loadReports(): Promise<IPaginatedResult<IReport>> {
        try {
            const items = await this.reportService.getAllItemsPaginated();
            return { items, totalItems: items.length, hasMore: false };
        } catch (err) {
            console.error('loadReports', err);
        }
    }

    private async loadTemplates(): Promise<IReportTemplate[]> {
        try {
            const items = await this.templateService.getAllItems();
            return items.sort((a, b) => new Date(b.modifiedAt).getTime() - new Date(a.modifiedAt).getTime()); // sort by modified date desc
        } catch (err) {
            console.error('loadTemplates', err);
        }
    }

    private async loadResponsible(): Promise<IResponsible> {
        try {
            return this.responsibleService.getById(this.selectedItem.id);
        } catch (err) {
            console.error('loadResponsible', err);
        }
    }

    private async loadUser(): Promise<IUser> {
        try {
            return this.userService.getById(this.selectedItem.id);
        } catch (err) {
            console.error('loadUser', err);
        }
    }

    private async loadItem(type: ToolbarTypeAdmin) {
        try {
            let promise: Promise<IBase | IBase[]>;
            switch (this.toolbarType) {
                case ToolbarTypeAdmin.Users:
                    promise = this.loadUser();
                    break;
                case ToolbarTypeAdmin.Hyperlinks:
                    break;
                case ToolbarTypeAdmin.Glossary:
                    promise = this.loadGlossary();
                    break;
                case ToolbarTypeAdmin.Reports:
                    promise = this.reportService.getItem(this.selectedItem.id);
                    break;
                case ToolbarTypeAdmin.ReportTemplates:
                    promise = this.loadTemplates();
                    break;
                case ToolbarTypeAdmin.Faq:
                    promise = this.loadFaq();
                    break;
                case ToolbarTypeAdmin.Responsibilities:
                    promise = this.loadResponsible();
                    break;
            }

            if (promise) {
                const result = await promise;
                if (result instanceof Array) {
                    this.data = result;
                    this.dataChanged.next(this.data);
                } else {
                    switch (this.toolbarType) {
                        case ToolbarTypeAdmin.Users:
                        case ToolbarTypeAdmin.Responsibilities:
                        case ToolbarTypeAdmin.Reports:
                        case ToolbarTypeAdmin.ReportTemplates:
                            this.selectedItem = result;
                            break;
                    }
                }
            }

            switch (this.toolbarType) {
                case ToolbarTypeAdmin.Faq:
                    this.selectedItem = this.adminFaqService.getItem(this.data, this.selectedItem.id);
                    break;
                case ToolbarTypeAdmin.Glossary:
                    this.selectedItem = this.data.find(e => e.id === this.selectedItem.id);
                    break;
            }
        } catch (err) {
            console.error('loadData', err);
        }

        this.selectedItemChanged.next(this.selectedItem);
    }
}

export enum ToolbarStateAdmin {
    Standard,
    Create,
    Info,
    Edit
}

export enum ToolbarTypeAdmin {
    Users = 'users',
    Responsibilities = 'responsibilities',
    Reports = 'reports',
    ReportTemplates = 'report-templates',
    Hyperlinks = 'hyperlinks',
    Glossary = 'glossary',
    Faq = 'faq'
}

export interface ToolbarInfoAdmin {
    state: ToolbarStateAdmin;
    type?: ToolbarTypeAdmin;
    noReload?: boolean;
    query?: string;
    helpFunction?: boolean;
    selectedItem?: any;
    items?: any[];
    sort?: boolean;
}
